import React from "react";
import { Box, Row, Col } from "jsxstyle";
import Button from "components/Button";
import Separator from "components/Separator";
import FormGrid from "components/FormGrid";
import FormGroup from "components/FormGroup";
import SelectEntityMaster from "components/SelectEntityMaster";
import CurrencyInputGroup from "components/CurrencyInputGroup";
import Form from "components/Form";
import Switch from "components/Switch";
import useToast from "hooks/useToast";
import useForm from "hooks/useForm";
import useAsync from "hooks/useAsync";
import saveCMBSServicerService from "services/cmbs/saveCMBSServicerService";
import CurrencyFormat from "components/CurrencyFormat";
import isRequired from "validators/isRequired";
import { get } from "lodash";

function CMBSServicersForm({
  onData,
  initialValues,
  onCancel,
  dealSize,
  masterServicerCredit,
  specialServicerCredit,
}) {
  const { showSuccess, showError } = useToast();

  const {
    getValue,
    setValue,
    setValues,
    getValues,
    getError,
    submit,
    reset,
    clear,
  } = useForm({
    onSubmit: (values) =>
      fetch({
        values,
        dealCode: values.dealCode,
        servicerId: values.id,
      }),
    initialValues: {
      ...(initialValues || {}),
      amount_percentage: Number(
        (((initialValues.amount || 0) / dealSize) * 100).toFixed(2),
      ),
    },
    validations: {
      servicer: [isRequired],
      amount: [isRequired],
    },
  });

  const [fetch, isSubmitting] = useAsync({
    promiseFn: saveCMBSServicerService,
    onData: () => {
      showSuccess("Servicer saved.");
      reset();
      onData();
    },
    onError: (error) => {
      const type = get(error, "data.error");
      showError(
        {
          duplicated_key:
            "Cannot add same Servicer twice in the same category to the same deal",
        }[type] || "An error happened.",
      );
    },
  });

  const handleCancel = React.useCallback(() => {
    onCancel();
    reset();
    clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEditing = Boolean(getValue("id"));

  return (
    <Form onSubmit={() => submit()}>
      <FormGrid>
        <Box className="col-4">
          <FormGroup
            label="Servicer"
            disabled={isSubmitting}
            error={getError("servicer")}
          >
            <SelectEntityMaster
              onSelect={(value) => setValue("servicer", value)}
              value={getValue("servicer")}
              error={getError("servicer")}
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            key={getValue("amountKey")}
            label="Amount"
            disabled={isSubmitting}
            error={getError("amount")}
          >
            <CurrencyInputGroup
              leftIcon="dollar"
              intent={getError("amount") ? "danger" : null}
              value={getValue("amount")}
              onChange={(value) =>
                setValues({
                  ...getValues(),
                  amount: value,
                  amount_percentage: Number(
                    ((value / dealSize) * 100).toFixed(2),
                  ),
                  percentageKey: getValue("percentageKey", 0) + 1,
                })
              }
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            key={getValue("percentageKey")}
            label="Amount(%)"
            disabled={isSubmitting}
            error={getError("amount_percentage")}
          >
            <CurrencyInputGroup
              leftIcon="percentage"
              value={getValue("amount_percentage")}
              onChange={(value) =>
                setValues({
                  ...getValues(),
                  amount: Number(((value * dealSize) / 100).toFixed(2)),
                  amount_percentage: value,
                  amountKey: getValue("amountKey", 0) + 1,
                })
              }
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-2">
          <FormGroup
            label="Special servicer"
            disabled={isSubmitting}
            error={getError("is_special")}
          >
            <Switch
              value={getValue("is_special")}
              onChange={() => setValue("is_special", !getValue("is_special"))}
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Row className="col-12">
          <Row flex={1} alignItems="flex-start">
            {isEditing && (
              <React.Fragment>
                <Button
                  text="Cancel"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                />
                <Separator />
              </React.Fragment>
            )}
            <Button
              type="submit"
              text={isEditing ? "Save" : "Add"}
              intent="success"
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </Row>
          <Col alignItems="flex-end">
            <Box component="p">
              Deal size:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize} />
              </Box>
            </Box>

            <Box component="p">
              Remaining master servicer credit:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize - masterServicerCredit} />
              </Box>
            </Box>

            <Box component="p">
              Remaining special servicer credit:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize - specialServicerCredit} />
              </Box>
            </Box>
          </Col>
        </Row>
      </FormGrid>
    </Form>
  );
}

CMBSServicersForm.propTypes = {};

CMBSServicersForm.defaultProps = {};

export default CMBSServicersForm;
